import React from 'react';
import { MenuListProvider } from './MenuListContext';
import MenuListItem from './MenuListItem';
import MenuListSection from './MenuListSection';
import * as S from './styles';
import { MenuListProps } from './types';

function ForwardedMenuList<As extends React.ElementType = 'ul'>(
  { as, density = 'default', ...props }: MenuListProps<As>,
  ref: React.ForwardedRef<React.ElementRef<As>>,
): React.ReactElement | null {
  const Component: React.ElementType = as ?? 'ul';

  return (
    <MenuListProvider density={density}>
      <S.MenuListRoot as={Component} {...props} ref={ref} $density={density} />
    </MenuListProvider>
  );
}

/**
 * Styled list UI to be used within menus.
 *
 * This component is used to render lists within menus - popover menus, account
 * menus, select menus, etc.  Conceptually, it performs the role of a listbox, however
 * a listbox is wired up in a specific way that doesn't fit all of the menu-list
 * use cases.
 *
 * This component does not implement any accessibilty and is not optimized for
 * cross-device use - that functionality should be added when using this component
 * to build higher level menus and lists.
 *
 * By default, the component will render the list of items as an unordered HTML
 * list using `ul` and `li` tags.  These tags can be replaced with any component
 * to change how the list is represented in the dom.
 */
const MenuListComponent = React.forwardRef(ForwardedMenuList) as <
  As extends React.ElementType = 'ul',
>(
  props: MenuListProps<As> & { ref?: React.Ref<React.ElementRef<As>> },
) => React.ReactElement | null;

export const MenuList = Object.assign(MenuListComponent, {
  Item: MenuListItem,
  Section: MenuListSection,
});

export default MenuList;
